import {
  DateSelectorInput,
  DropdownInput,
  GeoPointInput,
  RadioInput,
  TextAreaInput,
  TextInput,
} from "@validereinc/common-components";
import {
  AttributeDataType,
  type AttributeDataTypeType,
  type FormSchemaNormalQuestionType,
} from "@validereinc/domain";
import { Forms } from "@validereinc/domain-controllers";
import {
  hasNumericBoundsValue,
  validateDateBounds,
  validateLength,
  validateNumericBounds,
} from "@validereinc/utilities";
import React from "react";
import { FileField } from "./FileField";
import { LookupField } from "./LookupField";

const { DateTimeSubstitutions } = Forms.logic;

export const QuestionField = (
  props: Omit<FormSchemaNormalQuestionType, "type"> & {
    name: string;
  } & Partial<{
      lookup_entity_attribute?: string;
      questionId?: string;
    }>
) => {
  const {
    data_type,
    prompt,
    is_required,
    is_long_string,
    date_resolution,
    ...restProps
  } = props;

  const commonProps = {
    label: prompt,
    isRequired: is_required,
    isFluid: true,
    placeholder: `${([AttributeDataType.PICK_LIST, AttributeDataType.MULTI_PICK_LIST, AttributeDataType.LOOKUP, AttributeDataType.DATE, AttributeDataType.DATE_TIME, AttributeDataType.DATE_TIME_RANGE] as AttributeDataTypeType[]).includes(data_type) ? "Select" : "Enter"} ${prompt}...`,
    ...restProps,
  };
  const dateProps =
    restProps.before || restProps.after
      ? {
          max:
            restProps.before === DateTimeSubstitutions.$NOW
              ? new Date()
              : restProps.before
                ? new Date(restProps.before)
                : undefined,
          min:
            restProps.after === DateTimeSubstitutions.$NOW
              ? new Date()
              : restProps.after
                ? new Date(restProps.after)
                : undefined,
        }
      : {};

  const getStringLengthValidation = (
    value: string,
    dataType: string,
    minLength?: number,
    maxLength?: number
  ) => {
    return value &&
      dataType === AttributeDataType.STRING &&
      (minLength !== undefined || maxLength !== undefined)
      ? (validateLength(value, {
          min: minLength,
          max: maxLength,
        }) as string | true)
      : true;
  };

  return (
    <>
      {[
        AttributeDataType.NUMBER as string,
        AttributeDataType.INTEGER as string,
      ].includes(data_type) || !data_type ? (
        <TextInput
          {...commonProps}
          type={data_type === AttributeDataType.STRING ? "text" : "number"}
          rules={{
            validate: {
              numericBounds: (value: number) =>
                hasNumericBoundsValue(value) &&
                [
                  AttributeDataType.NUMBER as string,
                  AttributeDataType.INTEGER as string,
                ].includes(data_type) &&
                (restProps.minimum !== undefined ||
                  restProps.maximum !== undefined)
                  ? (validateNumericBounds(Number(value), {
                      min: restProps.minimum,
                      max: restProps.maximum,
                    }) as string | true)
                  : true,
            },
          }}
        />
      ) : null}
      {data_type === AttributeDataType.STRING && !is_long_string ? (
        <TextInput
          {...commonProps}
          type="text"
          rules={{
            validate: {
              length: (value: string) =>
                getStringLengthValidation(
                  value,
                  data_type,
                  restProps.min_length,
                  restProps.max_length
                ),
            },
          }}
        />
      ) : null}
      {data_type === AttributeDataType.STRING && is_long_string ? (
        <TextAreaInput
          {...commonProps}
          rules={{
            validate: {
              length: (value: string) =>
                getStringLengthValidation(
                  value,
                  data_type,
                  restProps.min_length,
                  restProps.max_length
                ),
            },
          }}
        />
      ) : null}
      {[
        AttributeDataType.DATE as string,
        AttributeDataType.DATE_TIME as string,
        AttributeDataType.DATE_TIME_RANGE as string,
      ].includes(data_type) ? (
        <DateSelectorInput
          {...commonProps}
          {...dateProps}
          {...(data_type === "date-time-range"
            ? { isRange: true, isArraySyntax: true }
            : {})}
          variant={
            data_type === "date"
              ? date_resolution === "year"
                ? "year"
                : date_resolution === "year_month"
                  ? "month"
                  : "day"
              : "time"
          }
          rules={{
            validate: {
              dateBounds: (value: string) =>
                value &&
                [
                  AttributeDataType.DATE as string,
                  AttributeDataType.DATE_TIME as string,
                ].includes(data_type) &&
                (restProps.before !== undefined ||
                  restProps.after !== undefined)
                  ? (validateDateBounds(new Date(value), {
                      before: restProps.before
                        ? restProps.before !== DateTimeSubstitutions.$NOW
                          ? new Date(restProps.before)
                          : restProps.before
                        : undefined,
                      after: restProps.after
                        ? restProps.after !== DateTimeSubstitutions.$NOW
                          ? new Date(restProps.after)
                          : restProps.after
                        : undefined,
                      fallbackBeforeDate:
                        restProps.before === DateTimeSubstitutions.$NOW
                          ? new Date()
                          : undefined,
                      fallbackAfterDate:
                        restProps.after === DateTimeSubstitutions.$NOW
                          ? new Date()
                          : undefined,
                      fallbackDateLabel: [
                        restProps.before,
                        restProps.after,
                      ].includes("$now")
                        ? data_type
                        : undefined,
                    }) as string | true)
                  : true,
            },
          }}
        />
      ) : null}

      {data_type === AttributeDataType.BOOLEAN ? (
        <RadioInput
          {...commonProps}
          options={[
            { label: "Yes", value: true },
            { label: "No", value: false },
          ]}
          labelKey="label"
          valueKey="value"
        />
      ) : null}

      {[
        AttributeDataType.PICK_LIST as string,
        AttributeDataType.MULTI_PICK_LIST as string,
      ].includes(data_type) ? (
        <DropdownInput
          {...commonProps}
          options={(props as FormSchemaNormalQuestionType).pick_list_values}
          isMulti={data_type === AttributeDataType.MULTI_PICK_LIST}
        />
      ) : null}

      {(
        [
          AttributeDataType.LOOKUP,
          AttributeDataType.MULTI_LOOKUP,
        ] as AttributeDataTypeType[]
      ).includes(data_type) ? (
        <LookupField
          lookup_entity_display_attributes={props.lookup_entity_attributes}
          isMulti={data_type === AttributeDataType.MULTI_LOOKUP}
          {...commonProps}
        />
      ) : null}

      {data_type === AttributeDataType.GEO_POINT ? (
        <GeoPointInput
          {...commonProps}
          isFluid
        />
      ) : null}

      {[
        AttributeDataType.FILE as string,
        AttributeDataType.MULTI_FILE as string,
      ].includes(data_type) ? (
        <FileField
          {...commonProps}
          allowMultiple={data_type === AttributeDataType.MULTI_FILE}
        />
      ) : null}
    </>
  );
};
